import { EventEmitter, OnDestroy } from '@angular/core';
import { Component, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { CustomViewService } from '../../services/custom-view.service';
import { ViewContext } from '../odata-filter/models/view-context';
import { ODataFilter, FilterUIType } from '../odata-filter/models/odata-filter';
import { SavedFilter } from '../odata-filter/models/saved-filter';
import { CustomView } from '../odata-filter/models/custom-view';
import { SelectedFilter } from '../odata-filter/models/selected-filter';
import { CustomViewColumn } from '../odata-filter/models/custom-view-column';
import { AuthService } from 'src/app/auth/auth.service';
import { BaseODataService } from '../../services/base-odata.service';
import { CustomViewDisplayLayout } from '../odata-filter/models/custom-view-display-layout';
import { AppConfigService } from '../../services/app-config.service';

@Component({
  selector: 'lineup-views-sidebar',
  templateUrl: './custom-view-sidebar.component.html',
  styleUrls: ['./custom-view-sidebar.component.scss'],
})
export class CustomViewSidebarComponent implements OnInit, OnDestroy {
  customViewDisplayLayoutEnum = CustomViewDisplayLayout;
  viewContextEnum = ViewContext;
  customViewDisplayLayout: CustomViewDisplayLayout;
  filters: ODataFilter[];
  @Input() title: string;
  selectedView: CustomView;
  appliedFilters: SelectedFilter[] = [];
  @Output() close = new EventEmitter();
  @Output() open = new EventEmitter();
  selectedFilters: SelectedFilter[] = [];
  isSaving = false;
  editMode = false;
  @Input() opened = false;
  @Input() filterService: BaseODataService;
  @Input() viewContextId?: string;
  viewContext: ViewContext;
  private quickFilterNames: string[];
  columns: CustomViewColumn[] = [];
  private subs: Subscription[] = [];
  public customViewForm: UntypedFormGroup;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private customViewService: CustomViewService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private appConfigService: AppConfigService
  ) {
    this.viewContext = route.snapshot.data['viewContext'];
  }
  get canManageColumns(): boolean {
    return this.appConfigService.tenant.plan.canManageColumns;
  }
  get showManageColumns(): boolean {
    return (
      (this.viewContext != ViewContext.InProgressPersonCredentialApplications &&
        this.viewContext != ViewContext.AllInProgressPersonCredentialApplications &&
        this.viewContext != ViewContext.PersonCredentialApplicationReport &&
        this.viewContext != ViewContext.People) ||
      this.customViewDisplayLayout == CustomViewDisplayLayout.Table
    );
  }
  ngOnInit(): void {
    if (!this.filterService) return;
    let sub = combineLatest([
      this.filterService.appliedFilters,
      this.filterService.filterCollection,
      this.filterService.quickFilterNames,
    ]);
    this.subs.push(
      sub.subscribe((results) => {
        if (results[0] && results[1] && results[2]) {
          this.updateFilters(results);
        }
      }),

      this.customViewService.customViewCreated.subscribe((result) => {
        this.isSaving = false;
        if (result) {
          this.onClose();
          this.opened = false;
        }
      }),
      this.customViewService.customViewUpdated.subscribe((result) => {
        this.isSaving = false;
        if (result) {
          this.onClose();
          this.opened = false;
        }
      })
    );
  }

  updateFilters(
    results: [
      appliedFilters: SelectedFilter[],
      filterCollection: ODataFilter[],
      quickFilterNames: string[]
    ]
  ) {
    this.filters = results[1];
    this.appliedFilters = results[0];
    this.selectedView = this.filterService.selectedView;
    this.quickFilterNames = results[2];
  }

  public create() {
    this.editMode = false;
    this.customViewForm = this.formBuilder.group({
      viewName: ['', Validators.required],
      isPublic: [false],
    });
    this.open.emit();
    this.opened = true;
    this.columns = this.selectedView.columns.map((c) => CustomViewColumn.parse(c));
    this.selectedFilters = this.appliedFilters.map((x) => new SelectedFilter(x));
    this.customViewDisplayLayout = this.selectedView.customViewDisplayLayout;
  }

  public edit(view: CustomView) {
    this.selectedView = view;
    this.editMode = true;
    this.customViewForm = this.formBuilder.group({
      viewName: [this.selectedView.name, Validators.required],
      isPublic: [this.selectedView.isPublic],
    });
    this.open.emit();
    this.opened = true;
    this.columns = this.selectedView.columns.map((c) => CustomViewColumn.parse(c));
    this.selectedFilters = SavedFilter.parseToAppliedFilters(
      this.selectedView.filters,
      this.selectedView.viewContext
    ).map((appliedFilter) => {
      let filter = this.filters.find(
        (x) =>
          x.displayName == appliedFilter.filter.displayName &&
          x.targetField == appliedFilter.filter.targetField &&
          x.filterGroup == appliedFilter.filter.filterGroup &&
          x.childFieldName == appliedFilter.filter.childFieldName
      );
      appliedFilter.filter = filter;
      return new SelectedFilter(appliedFilter);
    });
    this.customViewDisplayLayout = this.selectedView.customViewDisplayLayout;
  }
  onSave() {
    this.selectedFilters.forEach((item) => {
      item.isFilterApplied = true;
      this.cleanupFiltersBeforApply(item);
      if (item.hasSubFilter && item.isSubFilterAdded) {
        item.selectedSubFilters.forEach((subItem) => {
          subItem.isFilterApplied = true;
          this.cleanupFiltersBeforApply(subItem);
        });
      }
    });

    if (!this.isValid || !this.customViewForm.valid) return;

    this.isSaving = true;
    let customView = new CustomView({
      id: this.editMode ? this.selectedView.id : '',
      name: this.customViewForm.get('viewName').value,
      viewContext: this.viewContext,
      isPublic: this.customViewForm.get('isPublic').value,
      filters: this.acceptableFilters.map((x) => SavedFilter.parseToString(new SelectedFilter(x))),
      quickFilterNames: this.quickFilterNames,
      columns: this.columns,
      customViewDisplayLayout: this.customViewDisplayLayout,
      viewContextId: this.viewContextId,
    });
    customView.isDefault = this.selectedView.isDefault;
    if (this.editMode) {
      this.customViewService.update(customView);
    } else {
      this.customViewService.create(customView);
    }
  }
  onClose() {
    if (this.customViewForm) {
      this.customViewForm.reset();
      this.columns = [];
    }
    this.close.emit();
    this.opened = false;
  }
  addFilter() {
    this.selectedFilters.push(new SelectedFilter(null));
  }

  deleteFilter(index: number) {
    this.selectedFilters.splice(index, 1);
  }

  addSubFilter(selectedFilter: SelectedFilter) {
    selectedFilter.selectedSubFilters.push(new SelectedFilter(null));
  }

  deleteSubFilter(selectedFilter: SelectedFilter, index: number) {
    selectedFilter.selectedSubFilters.splice(index, 1);
  }

  protected get acceptableFilters(): SelectedFilter[] {
    return this.selectedFilters.filter((x) => x.isValid);
  }

  get isValid(): boolean {
    return this.selectedFilters.findIndex((x) => !x.isValid || !x.isSubFilterValid) == -1;
  }

  get isApplied(): boolean {
    return this.selectedFilters.findIndex((x) => !x.isFilterApplied || !x.isSubFilerApplied) == -1;
  }
  protected get isStaff(): boolean {
    return this.authService.isStaff();
  }

  private cleanupFiltersBeforApply(item: SelectedFilter) {
    //to make sure we do not send any value if filter uses an operator that does not need value.
    if (item.filterOperator && !item.filterOperator.isSearchValueNeeded) {
      item.searchValue = null;
      item.searchValues = [];
    }
    if (item.uiType != FilterUIType.Multiselect) {
      item.searchValues = [];
    } else {
      item.searchValue = null;
    }
  }
  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
